import type { DateRangePickerValue, DateRangeDraft } from './DateRangePicker.types';

const shortMonthFormat = new Intl.DateTimeFormat('en-US', { month: 'short' });
const displayDateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

export const monthLabels = Array.from({ length: 12 }, (_, month) =>
  shortMonthFormat.format(new Date(2026, month, 1)),
);

export const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function dateRangeValueToDraft(
  value: Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'>,
): DateRangeDraft | undefined {
  const from = parseDateValue(value.dateFrom);
  const to = parseDateValue(value.dateTo);
  return from || to ? { from, to } : undefined;
}

export function formatDateRangeLabel(value: Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'>) {
  if (!value.dateFrom || !value.dateTo) return 'Select date range';
  return `${formatDisplayDate(value.dateFrom)} - ${formatDisplayDate(value.dateTo)}`;
}

export function formatDateValue(date: Date) {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function shiftSingleDayRange(
  value: Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'>,
  days: number,
  maxDate?: Date,
) {
  if (value.dateFrom !== value.dateTo) return undefined;
  const date = parseDateValue(value.dateFrom);
  if (!date) return undefined;
  const shifted = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  if (maxDate && stripTime(shifted) > stripTime(maxDate)) return undefined;
  const shiftedValue = formatDateValue(shifted);
  return { dateFrom: shiftedValue, dateTo: shiftedValue, preset: '' };
}

export function formatDisplayDate(value: string) {
  const date = parseDateValue(value);
  if (!date) return value;
  return displayDateFormat.format(date);
}

export function formatMonthYear(date: Date) {
  return monthYearFormat.format(date);
}

export function getInitialVisibleMonth(value: Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'>) {
  return parseDateValue(value.dateTo) ?? parseDateValue(value.dateFrom) ?? new Date();
}

export function getVisibleMonthCells(visibleMonth: Date) {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const totalDays = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | undefined> = [];

  for (let index = 0; index < firstDay.getDay(); index += 1) {
    cells.push(undefined);
  }
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(undefined);
  }
  return cells;
}

export function isBetweenRange(date: Date, range: DateRangeDraft | undefined) {
  if (!range?.from || !range.to) return false;
  return stripTime(date) > stripTime(range.from) && stripTime(date) < stripTime(range.to);
}

export function isAfterDate(left: Date, right: Date) {
  return stripTime(left) > stripTime(right);
}

export function isSameDate(left: Date, right: Date | undefined) {
  if (!right) return false;
  return stripTime(left) === stripTime(right);
}

export function parseDateValue(value: string) {
  const parts = value.split('-').map(Number);
  const [year, month, day] = parts;
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
    return undefined;
  }
  return date;
}

function stripTime(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function isSelectableDateRange(
  value: Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'>,
  maxDate?: Date,
) {
  const from = parseDateValue(value.dateFrom);
  const to = parseDateValue(value.dateTo);
  return Boolean(from && to && from <= to && (!maxDate || !isAfterDate(to, maxDate)));
}
